/* DB-vendor fallback profile page — renders an applied/approved vendor
   straight from MongoDB instead of the hardcoded `VENDORS` map. Used when
   visiting `/vendor/:slug` for a vendor with a real DB entry. */
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import VendorReviews from "@/components/VendorReviews";
import { MediaFavoriteHeart } from "@/lib/favorites";
import { MuteToggleVideo } from "./MuteToggleVideo";

const SERVICE_TO_CATEGORY = {
  djs: "djs", dj: "djs",
  production: "production",
  "content creation": "content-creation",
  catering: "catering",
  "event planner": "event-planner",
  "videography/photography": "video-photo",
  "video & photo": "video-photo",
  "photography": "video-photo",
  decor: "decor",
  florist: "decor",
  "hair & makeup": "hair-makeup",
  "hair & make-up": "hair-makeup",
  outfits: "bride-101",
  "bride 101": "bride-101",
  "groom 101": "groom-101",
  entertainment: "djs",
};

const guessCategorySlug = (serviceTypes = []) => {
  for (const s of serviceTypes) {
    const k = (s || "").toLowerCase().trim();
    if (SERVICE_TO_CATEGORY[k]) return SERVICE_TO_CATEGORY[k];
  }
  return null;
};

export const DbVendorProfile = ({ vendor }) => {
  const cat = guessCategorySlug(vendor.service_types) || "vendor-listing";
  const orientation = vendor.media_orientation || "portrait";
  const mediaItems = (vendor.media_urls || []).map((u) => ({
    type: /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(u) ? "video" : "image",
    src: u,
  }));
  const profileSrc = vendor.profile_picture_url
    ? vendor.profile_picture_url.startsWith("http")
      ? vendor.profile_picture_url
      : `${process.env.REACT_APP_BACKEND_URL}${vendor.profile_picture_url}`
    : null;
  const initial = (vendor.company_name || "?").trim().charAt(0).toUpperCase();
  return (
    <>
      <section data-testid="db-vendor-hero" className="relative w-full">
        <div className="relative h-[50vh] min-h-[360px] w-full overflow-hidden bg-[var(--ld-ink)]">
          {mediaItems[0]?.type === "image" ? (
            <img
              src={mediaItems[0].src}
              alt={vendor.company_name}
              className="absolute inset-0 h-full w-full object-cover opacity-70"
            />
          ) : null}
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"
          />
          <div className="relative z-10 h-full flex items-end">
            <div className="mx-auto w-full max-w-7xl px-6 md:px-12 pb-12 md:pb-16">
              <Link
                to={cat === "vendor-listing" ? "/vendor-listing" : `/category/${cat}`}
                data-testid="vendor-back-link"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold)] hover:text-white transition-colors"
              >
                <ArrowLeft size={14} strokeWidth={1.6} />
                Back to Vendors
              </Link>
              <h1
                data-testid="vendor-name"
                className="mt-5 font-serif-display text-4xl sm:text-5xl md:text-6xl text-white font-medium leading-[1.05] flex items-center gap-5"
              >
                <span
                  data-testid="vendor-avatar"
                  className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 shrink-0 overflow-hidden border border-[var(--ld-gold)]/60 bg-[var(--ld-ink)] text-[var(--ld-gold)] flex items-center justify-center text-2xl md:text-4xl rounded-full"
                >
                  {profileSrc ? (
                    <img
                      src={profileSrc}
                      alt={vendor.company_name || ""}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span aria-hidden>{initial}</span>
                  )}
                </span>
                <span className="min-w-0 break-words">
                  {vendor.company_name}
                </span>
              </h1>
              {vendor.based_in?.length > 0 && (
                <p className="mt-3 text-[15px] text-white/80 italic font-serif-display">
                  {vendor.based_in.join(", ")}
                  {vendor.coverage ? ` · covers ${vendor.coverage}` : ""}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {mediaItems.length > 0 && (
        <section className="relative px-4 sm:px-6 md:px-12 pt-14 md:pt-20">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-10">
              <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                The Feed
              </span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
                Recent Work
              </h2>
              <div className="mx-auto mt-5 h-px w-16 bg-[var(--ld-gold)]/60" />
            </div>
            <ul
              className={`grid gap-1.5 sm:gap-2 ${
                orientation === "landscape"
                  ? "grid-cols-1 sm:grid-cols-2"
                  : "grid-cols-2 sm:grid-cols-3"
              }`}
            >
              {mediaItems.map((it, i) => (
                <li
                  key={it.src || i}
                  className={`relative ${
                    orientation === "landscape"
                      ? "aspect-[4/3]"
                      : "aspect-[3/4]"
                  } overflow-hidden bg-[var(--ld-ink)]/5 border border-[var(--ld-ink)]/10`}
                >
                  {it.type === "video" ? (
                    <MuteToggleVideo src={it.src} vendor_slug={vendor.slug} />
                  ) : (
                    <img
                      src={it.src}
                      alt=""
                      loading="lazy"
                      className="block w-full h-full object-cover"
                    />
                  )}
                  {vendor.slug && (
                    <MediaFavoriteHeart
                      vendor_slug={vendor.slug}
                      media_url={it.src}
                      media_type={it.type === "video" ? "video" : "image"}
                      size={13}
                      className="absolute top-2 right-2 h-8 w-8 z-10"
                      testid={`db-media-favorite-${vendor.slug}-${i}`}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {vendor.short_intro && (
        <section className="relative px-6 md:px-12 py-16 md:py-24">
          <div className="mx-auto max-w-3xl">
            <div className="text-center">
              <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                About
              </span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
                Meet {vendor.company_name}
              </h2>
              <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
            </div>
            <p className="mt-10 text-[15.5px] leading-[1.85] text-[var(--ld-ink)]/85 whitespace-pre-wrap">
              {vendor.short_intro}
            </p>
          </div>
        </section>
      )}

      {vendor.service_types?.length > 0 && (
        <section className="relative px-6 md:px-12 py-12 bg-white/40 border-y border-[var(--ld-border)]">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-8">
              <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                What&apos;s Included
              </span>
              <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl text-[var(--ld-ink)] font-medium">
                Services Provided
              </h2>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 max-w-2xl mx-auto">
              {vendor.service_types.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-3 text-[14.5px] text-[var(--ld-ink)]/90"
                >
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]">
                    <Check size={11} strokeWidth={2} />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            {vendor.starting_price && (
              <p className="mt-8 text-center text-[14px] text-[var(--ld-ink-muted)]">
                From <strong className="text-[var(--ld-ink)]">{vendor.starting_price}</strong>
              </p>
            )}
          </div>
        </section>
      )}

      <VendorReviews slug={vendor.slug} />

      <section className="relative px-6 md:px-12 py-20 md:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h3 className="font-serif-display text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Ready to book {vendor.company_name}?
          </h3>
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            {vendor.email && (
              <a
                href={`mailto:${vendor.email}`}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:bg-black transition-colors duration-300"
              >
                Email {vendor.company_name}
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            )}
            {vendor.contact_number && (
              <a
                href={`tel:${vendor.contact_number}`}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors duration-300"
              >
                {vendor.contact_number}
              </a>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
